import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import ReactMarkdown from "react-markdown";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1495020689067-958852a7765e?auto=format&fit=crop&w=1200&q=80";

export default function PatientBlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/blogs/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-card flex flex-col">
        <Navbar />
        <main className="flex-1 px-6 lg:px-10 py-8 max-w-3xl w-full mx-auto">
          <div className="h-96 bg-muted rounded-2xl animate-pulse" />
        </main>
      </div>
    );
  }
  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-card flex flex-col">
        <Navbar />
        <main className="flex-1 px-6 lg:px-10 py-8 max-w-3xl w-full mx-auto">
          <div className="bg-white border border-border rounded-2xl p-8 text-center">
            <div className="text-lg font-semibold">Article not found</div>
            <Link to="/patient/blogs" className="text-primary text-sm mt-2 inline-block hover:underline">← Back to articles</Link>
          </div>
        </main>
      </div>
    );
  }

  const dt = post.published_at ? new Date(post.published_at).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "";

  return (
    <div className="min-h-screen bg-card flex flex-col">
      <Navbar />
      <main data-testid="patient-blog-post" className="flex-1 px-6 lg:px-10 py-8 max-w-3xl w-full mx-auto">
        <Link to="/patient/blogs" data-testid="blog-back-link" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to articles
        </Link>

        <article>
          {post.cover_image_url && (
            <img
              src={post.cover_image_url}
              alt={post.title}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK; }}
              className="w-full h-72 object-cover rounded-3xl mb-6"
            />
          )}
          <h1 data-testid="blog-post-title" className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">{post.title}</h1>
          <div className="mt-3 flex items-center gap-3 text-sm text-muted-foreground">
            <span className="font-medium">{post.author}</span>
            {dt && (
              <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{dt}</span>
            )}
          </div>
          <div data-testid="blog-post-body" className="prose prose-slate max-w-none mt-6">
            <ReactMarkdown>{post.content_md}</ReactMarkdown>
          </div>
        </article>

        {post.related && post.related.length > 0 && (
          <section className="mt-12 border-t border-border pt-8">
            <h2 className="text-xl font-semibold tracking-tight mb-4">Related articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {post.related.map((r) => (
                <Link key={r.id} to={`/patient/blogs/${r.slug}`} data-testid={`related-blog-${r.slug}`} className="group bg-white border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all">
                  <img src={r.cover_image_url} alt="" className="w-full h-32 object-cover" />
                  <div className="p-3">
                    <div className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">{r.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
